import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoFull, LogoMark } from "@/components/Logo";
import { useStore } from "@/context/StoreContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS = {
  mottatt: { label: "Mottatt", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  aktiv: { label: "Aktiv", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  kansellert: { label: "Kansellert", cls: "text-red-300 border-red-400/40 bg-red-400/10" },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });

const orderName = (o) =>
  [o.first_name, o.last_name].filter(Boolean).join(" ") || o.name || "Ukjent";

const AdminLogin = () => {
  const { login } = useStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-3xl border border-white/[0.08] bg-[#0F1623] p-8 sm:p-10"
      >
        <div className="flex justify-center mb-6"><LogoMark size={48} /></div>
        <h1 className="font-display font-bold text-2xl text-center mb-8">Admin-innlogging</h1>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-email">E-post</Label>
            <Input id="admin-email" type="email" required data-testid="admin-email-input"
              className="bg-[#06090E] border-white/10"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Passord</Label>
            <Input id="admin-password" type="password" required data-testid="admin-password-input"
              className="bg-[#06090E] border-white/10"
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit-button"
            className="w-full font-display font-bold bg-cyan-400 hover:bg-cyan-300 text-[#06090E] py-3.5 rounded-full transition-colors disabled:opacity-50">
            {loading ? "Logger inn..." : "Logg inn"}
          </button>
          <Link to="/" data-testid="admin-login-home-link" className="block text-center text-sm text-slate-400 hover:text-cyan-300 transition-colors pt-1">
            Tilbake til forsiden
          </Link>
        </form>
      </motion.div>
    </div>
  );
};

export default function Admin() {
  const { user, authChecked, logout } = useStore();
  const [orders, setOrders] = useState(null);
  const [updating, setUpdating] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = () =>
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#06090E]" data-testid="auth-loading">
        <div className="animate-blink"><LogoMark size={56} /></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-[#06090E] grid-bg">
        <AdminLogin />
      </main>
    );
  }

  const setStatus = async (orderId, status) => {
    setUpdating(orderId);
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
      toast.success(`Status satt til «${STATUS[status].label}»`);
    } catch (e) {
      toast.error("Kunne ikke oppdatere status");
    } finally {
      setUpdating(null);
    }
  };

  const stats = orders
    ? {
        total: orders.length,
        nye: orders.filter((o) => o.status === "mottatt").length,
        aktive: orders.filter((o) => o.status === "aktiv").length,
        omsetning: orders.filter((o) => o.status !== "kansellert").reduce((s, o) => s + o.price, 0),
      }
    : null;

  return (
    <main className="min-h-screen bg-[#06090E] grid-bg" data-testid="admin-dashboard">
      <header className="glass border-b border-white/[0.08] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <Link to="/" data-testid="admin-logo"><LogoFull /></Link>
          <div className="flex items-center gap-4">
            <span className="mono text-[10px] tracking-[0.25em] uppercase text-[#FF4800] border border-[#FF4800]/30 bg-[#FF4800]/10 px-3 py-1 rounded-full">Admin</span>
            <span className="hidden sm:block text-sm text-slate-400">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-orange-400 transition-colors">
              <LogOut size={16} /> Logg ut
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="mono text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">Administrasjon</p>
              <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl" data-testid="admin-heading">Bestillinger</h1>
            </div>
            <button onClick={load} data-testid="admin-refresh-button"
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/40 px-5 py-2.5 rounded-full transition-all">
              <RefreshCw size={15} /> Oppdater
            </button>
          </div>
        </motion.div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="admin-stats">
            {[
              { label: "Totalt", value: stats.total, testid: "admin-stat-total" },
              { label: "Nye (mottatt)", value: stats.nye, testid: "admin-stat-new" },
              { label: "Aktive", value: stats.aktive, testid: "admin-stat-active" },
              { label: "Omsetning", value: `${stats.omsetning},-`, testid: "admin-stat-revenue" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/[0.08] bg-[#0F1623] px-6 py-5" data-testid={s.testid}>
                <p className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">{s.label}</p>
                <p className="font-display font-extrabold text-2xl sm:text-3xl text-slate-100">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 space-y-4">
          {orders === null && (
            <div className="rounded-3xl border border-white/[0.08] bg-[#0F1623] h-40 animate-pulse" data-testid="admin-loading" />
          )}

          {orders !== null && orders.length === 0 && (
            <div className="rounded-3xl border border-dashed border-white/15 bg-[#0F1623]/60 p-12 text-center" data-testid="admin-empty">
              <p className="text-slate-400">Ingen bestillinger ennå.</p>
            </div>
          )}

          {orders?.map((o, i) => {
            const st = STATUS[o.status] || STATUS.mottatt;
            return (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.4) }}
                className="rounded-2xl border border-white/[0.08] bg-[#0F1623] p-6"
                data-testid={`admin-order-row-${i}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="font-display font-bold text-lg" data-testid={`admin-order-customer-${i}`}>{orderName(o)}</h2>
                      <span className={`mono text-[10px] tracking-[0.18em] uppercase px-3 py-1 rounded-full border ${st.cls}`} data-testid={`admin-order-status-${i}`}>
                        {st.label}
                      </span>
                      {o.order_number && (
                        <span className="mono text-xs text-slate-500 tracking-wider" data-testid={`admin-order-number-${i}`}>{o.order_number}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {o.email} · {o.phone} · {fmtDate(o.created_at)}
                    </p>
                    <p className="text-sm text-slate-400 mt-2">
                      <span className="text-cyan-300">{o.plan_name}</span> ·{" "}
                      <span className="font-semibold text-slate-200" data-testid={`admin-order-price-${i}`}>{o.price},-</span>
                      {o.gps_id && <span className="mono text-xs text-slate-500"> · GPS-ID {o.gps_id}</span>}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{o.address}{o.postcode ? `, ${o.postcode} ${o.city || ""}` : ""}{o.notes ? ` · «${o.notes}»` : ""}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    {o.status !== "aktiv" && (
                      <button
                        onClick={() => setStatus(o.id, "aktiv")}
                        disabled={updating === o.id}
                        data-testid={`admin-set-active-${i}`}
                        className="text-sm font-semibold bg-emerald-400/10 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/20 px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                      >
                        Sett aktiv
                      </button>
                    )}
                    {o.status !== "mottatt" && o.status !== "kansellert" && (
                      <button
                        onClick={() => setStatus(o.id, "mottatt")}
                        disabled={updating === o.id}
                        data-testid={`admin-set-received-${i}`}
                        className="text-sm font-semibold border border-white/15 text-slate-300 hover:border-white/30 px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                      >
                        Sett mottatt
                      </button>
                    )}
                    {o.status !== "kansellert" && (
                      <button
                        onClick={() => setStatus(o.id, "kansellert")}
                        disabled={updating === o.id}
                        data-testid={`admin-set-cancelled-${i}`}
                        className="text-sm font-semibold border border-red-400/30 text-red-300/80 hover:bg-red-400/10 px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                      >
                        Kanseller
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <Link to="/" data-testid="admin-home-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 transition-colors mt-10">
          <ArrowLeft size={16} /> Til forsiden
        </Link>
      </div>
    </main>
  );
}
